// Shared data model used by both the Node backend and the React frontend.

import type { CaptureSpeakerMetadata } from "./captureSpeakers.ts";
import type {
  AudienceAuthorIdentityKind,
  AudienceIdentityMode,
  ParticipantIdentity,
  PublicAuthorDisplay,
} from "./audienceIdentity.ts";
import type { SessionSttConfig, SessionSttProvider } from "./sttConfig.ts";
import type { BoardTheme } from "./recaps/themeBoard.ts";

export type {
  AudienceAuthorIdentityKind,
  AudienceIdentityMode,
  ParticipantIdentity,
  PublicAuthorDisplay,
} from "./audienceIdentity.ts";

export type TranscriptSource = "mock" | "mic" | "manual" | "stt";

export interface TranscriptEntry {
  id: string;
  ts: number; // epoch ms
  speaker: string; // e.g. "Moderator", "Panelist A", "Audience", "Unknown"
  speakerKey?: string | null; // stable diarization key, e.g. "speaker_1"
  text: string;
  source: TranscriptSource;
  /** True when this transcript entry is finalized and should drive suggestions. */
  final: boolean;
  rawSpeaker?: string;
  rawSpeakerKey?: string | null;
  rawText?: string;
  corrected?: boolean;
  capture?: CaptureSpeakerMetadata | null;
}

export interface TranscriptCorrection {
  id: string;
  sessionId: string;
  transcriptEntryId: string;
  ts: number;
  speakerKeyOverride?: string | null;
  textOverride?: string;
  updatedAt: number;
}

export interface TranscriptSummary {
  totalEntries: number | null;
  oldestTs: number | null;
  newestTs: number | null;
}

export interface SpeakerMapping {
  id: string;
  sessionId: string;
  speakerKey: string;
  displayName: string;
  updatedAt: number;
}

export interface ObservedSpeaker {
  speakerKey: string;
  provisionalSpeaker: string;
  displayName: string | null;
  capture?: CaptureSpeakerMetadata | null;
}

export type SttProvider = SessionSttProvider;
export type SttSpeakerChoiceSource = "result" | "alternative" | "majority" | "first" | "unknown";
export type SttFlushKind = "segment" | "endpoint" | "finalized" | "finished";

export interface SttWordSpeakerRun {
  speaker: string | null;
  count: number;
}

export interface SttSpeakerCount {
  speaker: string;
  count: number;
}

interface SttDebugBase {
  provider: SttProvider;
  captureStreamId: string | null;
  segmentStartSec: number | null;
  segmentEndSec: number | null;
  chosenSpeakerLabel: string;
  chosenSpeakerKey: string;
  chosenSource: SttSpeakerChoiceSource;
  wordCount: number;
  diarizedWordCount: number;
  unknownWordCount: number;
  mixedSpeakers: boolean;
  wordSpeakerRuns: SttWordSpeakerRun[];
  wordSpeakerRunsTruncated: boolean;
  speakerCounts: SttSpeakerCount[];
}

export interface DeepgramDebugFields extends SttDebugBase {
  provider: "deepgram";
  resultSpeakerNumber: number | null;
  alternativeSpeakerNumber: number | null;
  majorityWordSpeakerNumber: number | null;
}

export interface SpeechmaticsDebugFields extends SttDebugBase {
  provider: "speechmatics";
}

export interface SonioxDebugFields extends SttDebugBase {
  provider: "soniox";
  flushKind: SttFlushKind;
  finalAudioProcMs: number | null;
  totalAudioProcMs: number | null;
}

export type SttDebugFields =
  | DeepgramDebugFields
  | SpeechmaticsDebugFields
  | SonioxDebugFields;

export type SttDebugChunk = SttDebugFields & {
  id: string;
  sessionId: string;
  transcriptEntryId: string;
  ts: number;
};

// Runtime source of truth for suggestion storage labels. See README -> "Suggestion taxonomy".
export const SUGGESTION_TYPE_VALUES = [
  "follow-up",
  "clarification",
  "missing-topic",
  "bring-in-another-speaker",
  "contrast",
  "audience-prompt",
  "theme-recap",
  "time-nudge",
  "underrepresented-perspective",
  "perspective",
  "reasoning",
  "tradeoff",
  "example",
] as const;

export type SuggestionType = (typeof SUGGESTION_TYPE_VALUES)[number];

export type SuggestionStatus =
  | "new"
  | "approved"
  | "dismissed"
  | "archived"
  | "used";

export interface Suggestion {
  id: string;
  ts: number;
  type: SuggestionType;
  title: string;
  /** The prompt the moderator could read aloud. Editable by the operator. */
  prompt: string;
  rationale: string;
  /** Short transcript snippets that motivated the suggestion. */
  evidence: string[];
  status: SuggestionStatus;
  provider: string; // e.g. "mock" or a model name
  /** Whether the operator edited the prompt away from the generated text. */
  edited: boolean;
  answerBlockStartTs?: number;
  checkpointThroughTs?: number;
  checkpointKind?: "initial" | "mid" | "handoff";
  checkpointId?: string;
  candidateOrdinal?: number;
  judgeRank?: number;
  categoryId?: string;
}

export type ReplayState = "idle" | "playing" | "paused" | "finished";
export type AudioTransportState = "disconnected" | "connecting" | "connected" | "error";
export type SttTransportState = "disabled" | "connecting" | "connected" | "degraded" | "error";
export type CaptureState = "running" | "paused" | "stopped";
export type SessionLifecycleStatus = "draft" | "active" | "closed" | "archived";

export interface CaptureClockInactiveInterval {
  start: number;
  end: number;
}

export interface SessionMeta {
  id: string;
  joinCode: string | null;
  status: SessionLifecycleStatus;
  startedAt: number;
  title: string;
  provider: string; // suggestion provider name
  transcriptSource: TranscriptSource;
}

export interface SessionSettings {
  /** When true, the moderator view receives a live transcript projection.
   * Default false keeps the moderator view presentation-safe. */
  moderatorTranscript: boolean;
  audience?: AudiencePolicy;
}

export interface SessionPanelist {
  name: string;
  bio: string;
  isModerator?: boolean;
}

export interface SessionGenerationConfig {
  purpose: string;
  themes: string[];
  thresholdSeconds: number;
  thresholdWords: number;
  judgeEnabled: boolean;
  aiDebugEnabled: boolean;
  panelists: SessionPanelist[];
}

export type RecapPipeline = "windows" | "episodes";

export interface SessionRecapConfig {
  enabled: boolean;
  minSeconds: number;
  minWords: number;
  pipeline: RecapPipeline;
  consolidationSpacingMs?: number;
}

export interface SessionState {
  meta: SessionMeta;
  transcript: TranscriptEntry[];
  speakerMappings: SpeakerMapping[];
  observedSpeakers: ObservedSpeaker[];
  suggestions: Suggestion[];
  notes: string;
  settings: SessionSettings;
  generation: SessionGenerationConfig;
  recap: SessionRecapConfig;
  stt: SessionSttConfig;
  replay: {
    state: ReplayState;
    index: number;
    total: number;
  };
  status: {
    micActive: boolean;
    micPaused: boolean;
    captureState: CaptureState;
    captureStateChangedAt: number | null;
    captureClockStartedAt: number | null;
    captureClockPausedMs: number;
    captureClockInactiveIntervals: CaptureClockInactiveInterval[];
    micLevel: number; // 0..1, reported by operator browser
    selectedInputDeviceId: string | null;
    selectedInputDeviceLabel: string | null;
    audioTransport: AudioTransportState;
    audioTransportError: string | null;
    sttTransport: SttTransportState;
    sttError: string | null;
    lastSttActivityAt: number | null;
    lastTranscriptAt: number | null;
    /** Increments when speaker mappings or transcript corrections change rendered history. */
    transcriptProjectionVersion?: number;
    lastSuggestionLatencyMs: number | null;
    connectedClients: number;
    /** True while the suggestion provider is processing the transcript. */
    generating: boolean;
    /** True while live recap generation is processing a transcript window. */
    liveRecapGenerating: boolean;
    /** True while recap consolidation is rewriting the consolidated summary. */
    consolidationGenerating: boolean;
    /** Durable timeline coverage state. This is independent of suggestion checkpoints. */
    recapCoverage?: RecapCoverageStatus | null;
    /** Earliest source timestamp of the recap suffix currently being rebuilt. */
    repairingFromTs?: number | null;
    /** Earliest source timestamp of recap cards waiting for correction repair. */
    repairPendingFromTs?: number | null;
    /** Number of recap cards waiting for correction repair. */
    repairPendingRecapCount?: number;
    /** ts of the newest transcript entry the last COMPLETED generation saw. */
    processedThroughTs: number | null;
    /** ts of the newest transcript entry the IN-FLIGHT generation is processing. */
    processingThroughTs: number | null;
    /** Latest transcript timestamp covered by a persisted suggestion checkpoint. */
    suggestionCheckpointThroughTs?: number | null;
    /** Durable suggestion workflow state, independent of capture runtime writes. */
    suggestionCoverage?: SuggestionCoverageStatus | null;
    /** Outcome of the last completed suggestion generation run. */
    lastGenerationSummary?: {
      finishedAt: number;
      durationMs: number;
      candidateCount: number;
      keptCount: number;
      skipReason?: string;
    } | null;
    generationPhase?: "generating" | "judging" | null;
    /** Consolidation cadence + review state for the Consolidated function-bar chip. */
    consolidation?: {
      lastConsolidatedAt: number | null;
      inputSectionCount: number;
      /** Terminal episode artifacts not yet included by the pointed snapshot. */
      pendingEpisodeCount: number;
      /** Authoritative timeline state; blocked coverage also blocks themes. */
      timelinePhase: RecapCoverageStatus["phase"];
      previewPending: boolean;
    } | null;
  };
}

export interface SuggestionCoverageStatus {
  phase: "accruing" | "generating" | "judging" | "retrying" | "blocked" | "caught_up";
  currentCheckpointId: string | null;
  currentCheckpointKind: "initial" | "mid" | "handoff" | "exchange" | "forced" | "legacy" | null;
  currentAttemptCount: number | null;
  assignedCheckpointCount: number;
  terminalCheckpointCount: number;
  pendingCheckpointCount: number;
  retryableCheckpointCount: number;
  blockedCheckpointCount: number;
  repairableBlockedCheckpointCount?: number;
  hasBlockedHistory?: boolean;
  structuralBlocked?: boolean;
  countsClamped: boolean;
  retryDueAt: number | null;
  latestAssignedThroughTs: number | null;
  latestTerminalThroughTs: number | null;
  accruingWordCount: number;
  accruingDurationMs: number;
  accruingStale: boolean;
  /** Provider + optional judge duration for the latest durable evaluation. */
  lastSuggestionLatencyMs: number | null;
  errorCode: string | null;
  errorSummary: string | null;
}

export interface RecapCoverageStatus {
  phase:
    | "accruing"
    | "generating"
    | "retrying"
    | "verifying"
    | "blocked"
    | "caught_up"
    | "finalizing";
  discoveredThroughTs: number | null;
  completedThroughTs: number | null;
  targetThroughTs: number | null;
  discoveredWindowCount: number;
  completedWindowCount: number;
  pendingWindowCount: number;
  retryableWindowCount: number;
  verificationWindowCount: number;
  blockedWindowCount: number;
  countsClamped: boolean;
  timelineLease: "none" | "active" | "expired";
  consolidationLease: "none" | "active" | "expired";
  finalizationStatus:
    | "discovering"
    | "generating_timeline"
    | "verifying"
    | "consolidating"
    | "complete"
    | "blocked"
    | null;
  finalizationTargetTs: number | null;
  errorCode: string | null;
  errorSummary: string | null;
  integrityCodes: string[];
}

export interface SessionHistoryItem {
  id: string;
  title: string;
  joinCode: string | null;
  kind: "test" | "rehearsal" | "live";
  status: SessionLifecycleStatus;
  startedAt: number;
  endedAt: number | null;
  active: boolean;
  transcriptPreview: string[];
  transcriptEntryCountClamped: number;
  suggestionCountClamped: number;
  staffPostCountClamped: number;
  participantPostCountClamped: number;
  canDelete: boolean;
  deleteBlockReason: string | null;
}

/** The narrowed projection the moderator view is allowed to see. */
export interface ModeratorPrompt {
  id: string;
  type: SuggestionType;
  prompt: string;
  status: "approved" | "used";
  approvedAt: number;
}

/** A minimal transcript line the moderator is allowed to see when the operator
 * has opted in. Never carries source/rationale/evidence. */
export interface ModeratorTranscriptEntry {
  id: string;
  ts: number;
  speaker: string;
  speakerKey?: string | null;
  text: string;
}

export interface ModeratorState {
  sessionTitle: string;
  prompts: ModeratorPrompt[];
  /** Whether the operator has enabled the live transcript for the moderator. */
  showTranscript: boolean;
  /** Present (and non-empty) only when showTranscript is true. */
  transcript: ModeratorTranscriptEntry[];
}

export type AudienceVisibilityMode = "queued" | "auto-shown";

export interface AudiencePolicy {
  participantJoinOpen: boolean;
  postingOpen: boolean;
  repliesEnabled: boolean;
  postVisibilityMode: AudienceVisibilityMode;
  replyVisibilityMode: AudienceVisibilityMode;
  identityMode: AudienceIdentityMode;
  publicAuthorDisplay: PublicAuthorDisplay;
  /** @deprecated Compatibility projection for clients predating identityMode. */
  anonymousAudience: boolean;
}

export interface AudienceIdentityTransition {
  targetMode: "fully_anonymous";
  status: "erasing" | "failed" | "completed";
  phase: "participants" | "posts" | "replies" | "complete";
  processedCount: number;
  startedAt: number;
  completedAt: number | null;
  failureSummary: string | null;
}

export type AudiencePostStatus = "queued" | "shown" | "answered" | "hidden" | "archived";
export type AudienceReplyStatus = "queued" | "shown" | "hidden" | "archived";
export type AudienceCategorySource = "ai" | "manual";
export type AudienceCategoryCandidateSource = "seed" | "topic";
export type AudienceAiStatus = "pending" | "ready" | "failed";
export type AudienceOrigin = "ai" | "human";
export type DiscussionStarterStatus = "draft" | "shown" | "archived";
export type AudienceArtifactKind = "category-summary" | "consolidation-suggestion";
export type AudienceArtifactStatus = "draft" | "shown" | "archived";
export type AudienceArtifactReviewState = "pending" | "ready" | "failed";
export type PanelRecapStatus = "draft" | "shown" | "archived";
export type PanelRecapTriggerKind = "threshold" | "long-turn" | "ongoing" | "manual" | "episode";
export type ClaimRelationKind = "new" | "extends" | "candidate_repeat" | "qualifies";
export type ClaimProjectionDisposition =
  | "shown_new"
  | "shown_extension"
  | "suppressed_repeat";
export type RecapCoverageWindowStatus =
  | "pending"
  | "generating"
  | "retryable_error"
  | "needs_verification"
  | "generated"
  | "non_substantive"
  | "blocked";
export type RecapCoverageSourceRole = "primary" | "context";
export type RecapFinalizationStatus =
  | "discovering"
  | "generating_timeline"
  | "verifying"
  | "consolidating"
  | "complete"
  | "blocked";
export type RoomThreadKind = "participant-post" | "staff-post" | "ai-post" | "discussion-starter";
export type RoomThreadOrigin = "participant" | "human" | "ai";
export type ProjectorMode =
  | "thread"
  | "artifact"
  | "board"
  | "join-qr"
  | "key-themes"
  | "key-themes-board";
export type ProjectorBoardSort = "oldest_unanswered" | "most_liked" | "most_replied";

export interface DiscussionStarter {
  id: string;
  sessionId: string;
  createdAt: number;
  shownAt?: number;
  body: string;
  origin: AudienceOrigin;
  sourceSuggestionId?: string;
  sourceAudiencePostId?: string;
  categoryId?: string;
  status: DiscussionStarterStatus;
  audienceLabel?: string;
}

export interface AudienceCategory {
  id: string;
  sessionId: string;
  label: string;
  slug: string;
  description?: string;
  colorIndex?: number;
  createdBy: AudienceOrigin;
  createdAt: number;
  updatedAt: number;
}

export interface AudienceCategoryCandidate {
  id: string;
  sessionId: string;
  label: string;
  slug: string;
  description?: string;
  source: AudienceCategoryCandidateSource;
  createdAt: number;
  updatedAt: number;
  lastCheckpointThroughTs?: number;
}

export interface AudienceSessionJoinInfo {
  sessionId: string;
  joinCode: string | null;
  sessionTitle: string;
  panelists: SessionPanelist[];
  participantJoinOpen: boolean;
  postingOpen: boolean;
  repliesEnabled: boolean;
  identityMode: AudienceIdentityMode;
  publicAuthorDisplay: PublicAuthorDisplay;
}

export interface AudienceParticipant {
  sessionId: string;
  participantKey: string;
  alias: string;
  joinedAt: number;
  lastSeenAt: number;
}

export interface AudienceJoinResult {
  session: AudienceSessionJoinInfo;
  participant: AudienceParticipant;
  identity: ParticipantIdentity;
}

export interface AudienceReply {
  id: string;
  sessionId: string;
  postId: string;
  createdAt: number;
  body: string;
  authorLabel: string | null;
  authorIdentityKind?: AudienceAuthorIdentityKind;
  authorKey: string;
  status: AudienceReplyStatus;
  likeCount: number;
  likedByParticipant?: boolean;
}

export interface AudiencePost {
  id: string;
  sessionId: string;
  createdAt: number;
  body: string;
  authorLabel: string | null;
  authorIdentityKind?: AudienceAuthorIdentityKind;
  authorKey: string;
  threadKind?: RoomThreadKind;
  threadOrigin?: RoomThreadOrigin;
  sourceSuggestionId?: string;
  staleSourceCheckpointId?: string;
  staleSourceAt?: number;
  staleSourceReason?: string;
  sourceDiscussionStarterId?: string;
  suggestionType?: SuggestionType;
  audienceLabel?: string;
  editableByStaff?: boolean;
  status: AudiencePostStatus;
  categoryId?: string;
  categorySource?: AudienceCategorySource;
  aiCategoryStatus?: AudienceAiStatus;
  replyCount: number;
  likeCount: number;
  threadLikeCount: number;
  lastActivityAt: number;
  likedByParticipant?: boolean;
  replies?: AudienceReply[];
}

export interface RoomThread extends AudiencePost {
  threadKind: RoomThreadKind;
  threadOrigin: RoomThreadOrigin;
}

export interface AudienceArtifact {
  id: string;
  sessionId: string;
  kind: AudienceArtifactKind;
  categoryId?: string;
  body: string;
  status: AudienceArtifactStatus;
  createdBy: AudienceOrigin;
  reviewState: AudienceArtifactReviewState;
  createdAt: number;
  updatedAt: number;
}

export interface PanelRecap {
  id: string;
  sessionId: string;
  status: PanelRecapStatus;
  createdAt: number;
  updatedAt: number;
  sourceStartTs: number;
  sourceEndTs: number;
  triggerKind: PanelRecapTriggerKind;
  provider: string;
  sections: RecapSection[];
  keyPoints?: string[];
  examples?: string[];
  differentViews?: string[];
  generationReason?: string;
  headline?: string;
  nonSubstantive?: boolean;
  densityViolation?: boolean;
  sourceEpisodeOrdinal?: number;
}

export interface RecapPoint {
  claimId?: string;
  text: string;
  subpoints: string[];
}

export interface EpisodeClaim {
  claimId: string;
  text: string;
  subpoints: string[];
  speaker: string;
  speakerKey?: string | null;
  relationKind: ClaimRelationKind;
  targetClaimId?: string;
  disposition: ClaimProjectionDisposition;
  sourceEpisodeOrdinal: number;
}

export interface EpisodeArtifact {
  headline: string | null;
  nonSubstantive: boolean;
  claims: EpisodeClaim[];
  densityViolation?: boolean;
}

export interface RecapSection {
  timeTs: number;
  timeLabel: string;
  speaker: string;
  speakerKey?: string;
  speakerEdited?: boolean;
  sourceEntryIds?: string[];
  sourceSpans?: Array<{
    transcriptEntryId: string;
    startOffset: number;
    endOffset: number;
  }>;
  points: RecapPoint[];
}

export interface RecapTension {
  description: string;
  speakers: string[];
  timeRange: string;
  rangeStartTs: number;
  rangeEndTs: number;
}

export interface ConsolidationThemeTension {
  description: string;
  speakers: string[];
}

export interface ConsolidationTheme {
  title: string;
  synthesis: string;
  speakers: string[];
  tensions: ConsolidationThemeTension[];
  themeId?: string;
  mergedFromThemeIds?: string[];
  projectorSummary?: string;
  projectorFirstSeenAt?: number;
  projectorLastChangedAt?: number;
  projectorRevision?: number;
}

export interface DiscussionArcPoint {
  point: string;
  subpoints: string[];
}

export type StoredDiscussionArcPoint = string | DiscussionArcPoint;

export interface RecapConsolidationDraft {
  baseConsolidationId: string | null;
  attributionRevision: number;
  throughTs: number;
  throughWindowOrdinal?: number;
  provider: string;
  inputSectionCount: number;
  inputFingerprint: string;
  narrative: DiscussionArcPoint[];
  themes: ConsolidationTheme[];
}

export interface PendingRecapConsolidationPreview extends RecapConsolidationDraft {
  id: string;
  createdAt: number;
  stale: boolean;
}

export interface RecapConsolidationPreviewState {
  status: "none" | "generating" | "regenerating" | "ready";
  preview: PendingRecapConsolidationPreview | null;
}

export interface RecapConsolidationFreshness {
  behindLatestRecap: boolean;
  latestRecapSourceEndTs: number | null;
  latestConsolidationThroughTs: number | null;
  newerSectionCount: number;
  newerSectionCountLabel: string | null;
}

export interface RecapConsolidation {
  id: string;
  sessionId: string;
  consolidatedAt: number;
  throughTs: number;
  throughWindowOrdinal?: number;
  provider: string;
  inputSectionCount: number;
  inputFingerprint?: string;
  publishKind?: "automatic" | "manual_regenerate";
  supersedesConsolidationId?: string;
  supersededAt?: number;
  supersededByConsolidationId?: string;
  staleAt?: number;
  staleReason?: string;
  projectorThemeIds?: string[];
  themeIdFallback?: boolean;
  kind: "legacy" | "thematic";
  narrative: DiscussionArcPoint[];
  themes: ConsolidationTheme[];
  sections: RecapSection[];
  tensions: RecapTension[];
  freshness?: RecapConsolidationFreshness;
}

export type FeaturedAudienceItem =
  | { type: "discussionStarter"; item: DiscussionStarter }
  | { type: "post"; item: AudiencePost }
  | { type: "reply"; item: AudienceReply; parentPost: AudiencePost }
  | { type: "artifact"; item: AudienceArtifact }
  | null;

export type ProjectedContent =
  | {
      type: "thread";
      thread: RoomThread;
      highlightReplyId?: string;
      showReplies: boolean;
    }
  | { type: "artifact"; item: AudienceArtifact }
  | null;

export interface ProjectorState {
  sessionId: string;
  mode: ProjectorMode;
  projectedThreadId?: string;
  projectedArtifactId?: string;
  highlightReplyId?: string;
  showReplies: boolean;
  boardCategoryIds?: string[];
  boardSort: ProjectorBoardSort;
  updatedAt: number;
  updatedBy: "operator" | "moderator";
}

export interface ProjectorProjection {
  sessionTitle: string;
  joinCode: string | null;
  panelists: SessionPanelist[];
  policy: AudiencePolicy;
  projector: ProjectorState;
  projectedContent: ProjectedContent;
  boardThreads: RoomThread[];
}

export interface KeyThemesProjectionTheme {
  themeId: string | null;
  title: string;
  summary: string | null;
}

export interface CurrentDiscussion {
  headline: string;
  speakerKeys: string[];
  speaker?: string;
  mainPoint: string;
  subpoints: string[];
  previous?: { mainPoint: string; speakerKeys: string[] };
  lastChangedAt: number;
  throughEpisodeOrdinal: number;
}

export interface ListeningState {
  lastEntryTs: number;
  speaker: string;
}

export interface KeyThemesProjection {
  currentSections: RecapSection[];
  currentDiscussion?: CurrentDiscussion | null;
  latestSectionTs: number | null;
  timelineThroughEpisodeOrdinal?: number | null;
  themesThroughEpisodeOrdinal?: number | null;
  themes: KeyThemesProjectionTheme[];
  boardThemes: BoardTheme[];
  themeIdFallback: boolean;
  themesThroughTs: number | null;
  themesConsolidatedAt: number | null;
}

export interface ParticipantPendingReply {
  reply: AudienceReply;
  parentThread: RoomThread;
}

export interface ParticipantState {
  sessionId: string;
  sessionTitle: string;
  alias: string | null;
  participantKey: string | null;
  identity: ParticipantIdentity | null;
  policy: AudiencePolicy;
  projectedContent: ProjectedContent;
  categories: AudienceCategory[];
  threads: RoomThread[];
  pendingThreads: RoomThread[];
  pendingReplies: ParticipantPendingReply[];
  recaps: PanelRecap[];
}

export interface AdminAudienceState {
  sessionId: string;
  joinCode: string | null;
  policy: AudiencePolicy;
  identityTransition: AudienceIdentityTransition | null;
  categories: AudienceCategory[];
  categoryCandidates: AudienceCategoryCandidate[];
  threads: RoomThread[];
  artifacts: AudienceArtifact[];
  recaps: PanelRecap[];
  projector: ProjectorState | null;
}

export interface ModeratorAudienceState {
  sessionId: string;
  joinCode: string | null;
  policy: AudiencePolicy;
  categories: AudienceCategory[];
  categoryCandidates: AudienceCategoryCandidate[];
  threads: RoomThread[];
  artifacts: AudienceArtifact[];
  recaps: PanelRecap[];
  projector: ProjectorState | null;
}

// ---- WebSocket envelope ----
export type ServerMessage =
  | { type: "state"; payload: SessionState }
  | { type: "moderator"; payload: ModeratorState };

export interface ClientHello {
  role: "operator" | "moderator";
}
